import Box from "@mui/material/Box"
import CSSBaseline from "@mui/material/CssBaseline"
import { styled } from "@mui/material/styles"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Outlet, useLocation, useNavigate } from "react-router-dom"

import Column from "../../common/components/Column"
import { BLOCK_AFTER_STYLES } from "../../common/components/Footer"
import GlassMixin, {
  GlassMixinVariant2,
} from "../../common/components/GlassMixin"
import GlobalSnackbar from "../../common/components/GlobalSnackbar"
import Row from "../../common/components/Row"
import { isSmsOnly } from "../users/accountRole"
import { DISABLED_AFTER_TRIAL_ENDS_CLASS_NAME } from "../users/billing"
import {
  retrieveUserProfile,
  selectProfile,
  selectToken,
} from "../users/reducer"
import { selectProfileLoaded, selectSubscription } from "../users/reducer"
import MobileAppBar from "./MobileAppBar"
import { GlobalCallProvider } from "./calling/GlobalCallProvider"
import { PowerDialerProvider } from "./calling/PowerDialerProvider"
import ConciergeActiveHeartbeat from "./components/ConciergeActiveHeartbeat"
import ConciergeGreetingRequiredDialog from "./components/ConciergeGreetingRequiredDialog"
import NavbarRightSection from "./components/NavbarRightSection"
import RefreshEmailProviderDialog from "./components/RefreshEmailProviderDialog"
import SidebarLinks, {
  FREE_SIDEBAR_LINKS,
  SIDEBAR_WIDTH_COLLAPSED,
} from "./components/SidebarLinks"
import TrialFinishedRoadblockDialog, {
  TrialFinishedRoadblockDialogData,
} from "./components/TrialFinishedRoadblockDialog"
import GlobalSearchField from "./components/global-search-field/GlobalSearchField"
import { InboxNotificationsProvider } from "./components/inboxNotifications/InboxNotificationsContext"
import {
  openTrialFinishedRoadblockDialog,
  retrieveCRMSettings,
  retrieveEmailAuthorizationStatus,
  retrieveNotifications,
  selectAuthorizedEmailProviderRefreshFailed,
} from "./reducer"
import { isMlsPurchaseFlow } from "./trialGate"

export default function CustomerLayout() {
  const token = useSelector(selectToken)
  const [refreshEmailProviderDialogOpen, setRefreshEmailProviderDialogOpen] =
    useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const authorizedEmailProviderRefreshFailed = useSelector(
    selectAuthorizedEmailProviderRefreshFailed
  )
  const profileLoaded = useSelector(selectProfileLoaded)
  const subscription = useSelector(selectSubscription)
  const profile = useSelector(selectProfile)

  useEffect(() => {
    dispatch(retrieveUserProfile())
    dispatch(retrieveEmailAuthorizationStatus())
    dispatch(retrieveCRMSettings())
    dispatch(retrieveNotifications())
    const intervalId = window.setInterval(() => {
      dispatch(retrieveNotifications())
    }, 60000)
    return () => window.clearInterval(intervalId)
  }, [dispatch])

  useEffect(
    () => {
      // after dispatch removeToken is finished
      if (token === null) {
        navigate(`/user/log-in?next=${location.pathname}${location.search}`)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  )

  // Texting-only employees have just the SMS inbox; the API refuses the rest.
  useEffect(() => {
    if (profileLoaded && isSmsOnly(profile)) {
      navigate("/texts", { replace: true })
    }
  }, [navigate, profile, profileLoaded])

  useEffect(() => {
    if (authorizedEmailProviderRefreshFailed) {
      setRefreshEmailProviderDialogOpen(true)
    }
  }, [authorizedEmailProviderRefreshFailed])

  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }
      // currently used to block only sidebar links
      if (subscription && subscription.trialFinished) {
        document
          .querySelectorAll(`.${DISABLED_AFTER_TRIAL_ENDS_CLASS_NAME}`)
          .forEach((element) => {
            element.addEventListener("click", (event) => {
              event.preventDefault()
              const eventTargetDataset = event.currentTarget.dataset
              dispatch(
                openTrialFinishedRoadblockDialog(
                  new TrialFinishedRoadblockDialogData(
                    eventTargetDataset.featureName,
                    eventTargetDataset.subscribeReason
                  )
                )
              )
            })
          })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, profileLoaded]
  )

  // redirect to candidates page if user with trial ended tries to access blocked pages
  // by entering URL directly in URL bar (nerdy user possibly wearing glasses)
  // (FREE_SIDEBAR_LINKS: Candidates, Messages, Strategy Center, Settings)
  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }
      if (subscription && subscription.trialFinished) {
        let onFreePage = false
        for (const sidebarLink of Object.values(FREE_SIDEBAR_LINKS)) {
          if (location.pathname.startsWith(sidebarLink.url)) {
            onFreePage = true
            break
          }
        }
        if (
          !onFreePage &&
          !isMlsPurchaseFlow(location.pathname, location.search)
        ) {
          navigate("/candidates?subRedirect=1")
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, profileLoaded]
  )

  return (
    <GlobalCallProvider>
      <PowerDialerProvider>
        <InboxNotificationsProvider>
          <Box sx={{ minHeight: "100vh", position: "relative" }}>
            <CSSBaseline />
            <MobileAppBar />
            <Box
              sx={{
                position: "relative",
                overflow: "auto",
              }}
            >
              <SidebarLinks />
              <RightColumn>
                <HeaderRow>
                  <GlobalSearchField width="40%" maxWidth="640px" />
                  <NavbarRightSection />
                </HeaderRow>
                <ColumnBox>
                  <Outlet />
                </ColumnBox>
              </RightColumn>
            </Box>
            <GlobalSnackbar />
            <RefreshEmailProviderDialog
              open={refreshEmailProviderDialogOpen}
              onClose={() => setRefreshEmailProviderDialogOpen(false)}
            />
            <TrialFinishedRoadblockDialog />
            <ConciergeGreetingRequiredDialog />
            <ConciergeActiveHeartbeat />
          </Box>
        </InboxNotificationsProvider>
      </PowerDialerProvider>
    </GlobalCallProvider>
  )
}

const RightColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(2, 4),
  position: "absolute",
  top: 0,
  left: SIDEBAR_WIDTH_COLLAPSED,
  width: `calc(100vw - ${SIDEBAR_WIDTH_COLLAPSED} - var(--power-dialer-sidebar-width, 0px) - 6px)`,
  transition: theme.transitions.create("width", {
    duration: theme.transitions.duration.shortest,
  }),
  ...BLOCK_AFTER_STYLES,
  [theme.breakpoints.only("xs")]: {
    position: "static",
    left: 0,
    width: "100%",
    padding: theme.spacing(2, 2),
  },
}))

const HeaderRow = styled(Row)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  paddingInline: theme.spacing(1),
  paddingBlock: theme.spacing(1),
  position: "sticky",
  top: 0,
  zIndex: 4,
  borderRadius: "16px",
  [theme.breakpoints.only("xs")]: {
    display: "none",
  },
  ...GlassMixinVariant2,
}))

const ColumnBox = styled(Column)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: theme.spacing(2),
  ...GlassMixin,
  boxShadow: "0 4px 30px rgba(18, 16, 16, 0.1)",
  [theme.breakpoints.only("xs")]: {
    marginTop: "0px",
  },
}))
